import { Modal } from "@ttc3k/trekker";
import { PageError } from "../PageError";
import { useBookerContext, useCartContext } from "../../contexts";
import { SingleProductModalContent } from "./SingleProductModalContent";
import { BOOKER_DIALOG_ID, CatalogPaneStyles } from "../MerchCatalog";
import { formatPrice } from "shared/utils";
import { EnumBookerStatus, EnumProductType, EnumRuleType } from "gql/generated";
import { isPast, isFuture } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePostHog } from "shared/hooks";
import { PostHogErrorBoundary } from "posthog-js/react";
import { useTranslation } from "react-i18next";

type SingleProductModalProps = {
  open: boolean;
};

export const SingleProductModal = ({
  open: isOpen,
}: SingleProductModalProps) => {
  const { resetCartValuesToDefault, grandTotal, discounts } = useCartContext();
  const {
    onCloseBookerModal,
    booker,
    product,
    isEmbedded,
    bookerPosition,
    disableResetOnOpen,
    paymentRequestGrandTotal,
    catalogLayout,
  } = useBookerContext();
  const { getPostHogErrorCaptureData } = usePostHog();

  const [catalogCheckoutOpen, setCatalogCheckoutOpen] = useState(false);

  useEffect(() => {
    if (isOpen) setCatalogCheckoutOpen(false);
  }, [isOpen]);

  const timeoutRef = useRef<number | null>(null);

  // This handles a weird case in Dev mode in the storefront where the cart does
  // not correctly reflect updated guest price tier information
  useEffect(() => {
    if (disableResetOnOpen) return;
    if (isOpen) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        resetCartValuesToDefault();
      }, 50);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [disableResetOnOpen, isOpen, resetCartValuesToDefault]);

  const handleCloseModal = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setTimeout(() => {
      if (!disableResetOnOpen) {
        resetCartValuesToDefault();
      }
      onCloseBookerModal();
    }, 300);
  };

  const { t } = useTranslation("booker");

  const isBookerClosed = useMemo(() => {
    const maxBookableDate: undefined | string = booker?.rules.find(
      rule => rule.type === EnumRuleType.MaxBookableDate,
    )?.value;
    const minBookableDate: undefined | string = booker?.rules.find(
      rule => rule.type === EnumRuleType.MinBookableDate,
    )?.value;
    const isMainProductFee =
      product?.type === EnumProductType.Fee ||
      product?.type === EnumProductType.Merch;

    return (
      (maxBookableDate && isPast(new Date(maxBookableDate))) ||
      (booker &&
        [EnumBookerStatus.Closed, EnumBookerStatus.Archived].includes(
          booker.status,
        )) ||
      (isMainProductFee && minBookableDate
        ? isFuture(new Date(minBookableDate))
        : false)
    );
  }, [booker, product]);

  const displayTotal = paymentRequestGrandTotal ?? grandTotal;
  const discountTotal = useMemo(
    () => discounts.reduce((sum, d) => sum + (d.total ?? 0), 0),
    [discounts],
  );
  const showPill =
    displayTotal > 0 && (!isEmbedded || !isBookerClosed);

  const tagText = usePillCountdown(
    showPill ? displayTotal : 0,
    discountTotal,
    t("CHECKOUT.DISCOUNT_APPLIED"),
  );

  return (
    <Modal
      open={isOpen}
      placement={bookerPosition === "right" ? "rightPane" : "leftPane"}
      onOpenChange={({ open }: { open: boolean }) => {
        if (!open) handleCloseModal();
      }}
      tagText={tagText || undefined}
      closeTriggerVisual="light"
      closeTriggerPlacement="booker"
      ids={{ content: BOOKER_DIALOG_ID }}
      portal
    >
      {catalogLayout === "grid" && <CatalogPaneStyles />}
      <PostHogErrorBoundary
        fallback={<PageError />}
        additionalProperties={getPostHogErrorCaptureData({
          fileName: "SingleProductModal.tsx",
          extra: {
            bookerId: booker?._id ?? "No booker ID",
            errorBoundaryMessage:
              "SingleProductModal error boundary ... <PageError /> displayed",
          },
        })}
      >
        <SingleProductModalContent
          isBookerClosed={isBookerClosed}
          catalogCheckoutOpen={catalogCheckoutOpen}
          onCatalogCheckoutOpenChange={setCatalogCheckoutOpen}
        />
      </PostHogErrorBoundary>
    </Modal>
  );
};

/**
 * Manages the pill text. When a discount is newly applied (discountCents goes
 * from 0 to positive), shows a three-phase animation:
 *  1. Hold the pre-discount total (grandTotal + discount) for 400ms
 *  2. Count down to the discounted total over 800ms, with "Discount applied!"
 *     shown alongside for 1.2s
 * Otherwise updates instantly.
 */
function usePillCountdown(
  totalCents: number,
  discountCents: number,
  discountLabel: string,
): string {
  const [display, setDisplay] = useState("");
  const prevDiscountRef = useRef(discountCents);
  const animRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fmt = useCallback(
    (cents: number) => (cents > 0 ? `$${formatPrice(cents)}` : ""),
    [],
  );

  useEffect(() => {
    const prevDiscount = prevDiscountRef.current;
    prevDiscountRef.current = discountCents;

    // Cancel any running animation.
    cancelAnimationFrame(animRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);

    // A discount was just applied (went from 0 or less to a positive value).
    const discountJustApplied =
      discountCents > 0 && prevDiscount <= 0 && totalCents > 0;

    if (!discountJustApplied) {
      setDisplay(fmt(totalCents));
      return;
    }

    // The pre-discount total is the current discounted total plus the discount.
    const preDiscountTotal = totalCents + discountCents;

    // Phase 1: hold the pre-discount total.
    setDisplay(fmt(preDiscountTotal));

    timerRef.current = setTimeout(() => {
      // Phase 2: count down with label alongside.
      const start = performance.now();
      const duration = 800;
      const labelDuration = 1400;
      const labelStart = performance.now();

      const step = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - (1 - progress) * (1 - progress);
        const current = Math.round(
          preDiscountTotal + (totalCents - preDiscountTotal) * eased,
        );
        const showLabel = now - labelStart < labelDuration;
        setDisplay(
          showLabel
            ? `${fmt(current)} · ${discountLabel}`
            : fmt(current),
        );
        if (progress < 1 || showLabel) {
          animRef.current = requestAnimationFrame(step);
        }
      };
      animRef.current = requestAnimationFrame(step);
    }, 400);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      cancelAnimationFrame(animRef.current);
    };
  }, [totalCents, discountCents, discountLabel, fmt]);

  return display;
}
